
import React, { useState } from "react";
import Header from "@/components/Header";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import HeroSection from "@/components/HeroSection";
import LogoBanner from "@/components/LogoBanner";
import KeyEventSection from "@/components/KeyEventSection";
import CollectionIntroSection from "@/components/CollectionIntroSection";
import HowToSteps from "@/components/HowToSteps";

import TotalControlSection from "@/components/TotalControlSection";

import OurPromise from "@/components/OurPromise";
import TestimonialsSection from "@/components/TestimonialsSection";
import FundraisingSection from "@/components/FundraisingSection";
import Footer from "@/components/Footer";
import LeadFormModal from "@/components/LeadGenerationForm/LeadFormModal";
import EmailOnlyMissingModal from "@/components/LeadGenerationForm/EmailOnlyMissingModal";
import { Toaster } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";
import { sendSlackMessage } from "@/lib/sendSlackMessage";
import { getStoredUTMParams } from "@/lib/analytics";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";
import { useScrollTracking } from "@/hooks/useScrollTracking";

const Index = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [showEmailOnlyMissing, setShowEmailOnlyMissing] = useState(false);
  const [savedFormData, setSavedFormData] = useState<any>(null);
  const [formTrigger, setFormTrigger] = useState({ location: "unknown", text: "unknown" });
  
  // Enable scroll tracking
  useScrollTracking();

  const formatBudget = (budgetRange: string): string => {
    switch (budgetRange) {
      case "10k_or_less":
        return "$10K or less";
      case "10k_50k":
        return "$10K-$50K";
      case "50k_100k":
        return "$50K-$100K";
      case "100k_plus":
        return "$100K+";
      default:
        return budgetRange as string;
    }
  };

  const formatEventType = (eventType: string): string => {
    switch (eventType) {
      case "corporate_retreat":
        return "Corporate Retreat";
      case "away_day":
        return "Away Day";
      case "corporate_party":
        return "Corporate Party";
      case "team_building":
        return "Team Building";
      case "conference":
        return "Conference";
      case "product_launch":
        return "Product Launch";
      case "other":
        return "Other";
      default:
        return eventType as string;
    }
  };

  const handleFormClose = (formData: any) => {
    // Sauvegarder les données du formulaire
    setSavedFormData(formData);
    
    // Vérifier si seul l'email manque après la fermeture du formulaire
    if (formData.isOnlyEmailMissing) {
      // Attendre 2 secondes puis afficher le modal email manquant
      setTimeout(() => {
        setShowEmailOnlyMissing(true);
      }, 2000);
    }
  };

  const handleEmailOnlyMissingContinue = () => {
    setShowEmailOnlyMissing(false);
    // Rouvrir le formulaire directement à l'étape 6 avec les données sauvegardées
    setIsFormOpen(true);
  };

  const handleEmailOnlyMissingClose = () => {
    setShowEmailOnlyMissing(false);
    setSavedFormData(null);
  };

  const handleEmailSubmit = async (email: string) => {
    if (!savedFormData || !savedFormData.eventDetails) {
      toast.error("Erreur: Données du formulaire manquantes");
      return;
    }

    try {
      const eventDetails = savedFormData.eventDetails;
      const budgetFormatted = formatBudget(eventDetails.budget);
      
      let startDateToSave = eventDetails.startDate;
      let endDateToSave = eventDetails.endDate;
      const isMonthSelection = eventDetails.month && eventDetails.month.trim() !== "" && (!eventDetails.startDate || eventDetails.startDate.trim() === "");
      
      if (isMonthSelection) {
        startDateToSave = eventDetails.month;
        endDateToSave = eventDetails.month;
      }

      const leadData = {
        id: uuidv4(),
        eventtype: eventDetails.eventType,
        startdate: startDateToSave,
        enddate: endDateToSave,
        participantscount: eventDetails.participantsCount,
        budget: budgetFormatted,
        location: eventDetails.location,
        workemail: email,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase.from('alert-form-fr').insert(leadData);

      if (error) throw error;

      const eventTypeDisplay = formatEventType(eventDetails.eventType);
      let dateDisplay = "";
      
      if (isMonthSelection) {
        dateDisplay = `${eventDetails.month}`;
      } else if (eventDetails.startDate && eventDetails.endDate) {
        dateDisplay = `du ${eventDetails.startDate} au ${eventDetails.endDate}`;
      } else if (eventDetails.startDate) {
        dateDisplay = `le ${eventDetails.startDate}`;
      } else {
        dateDisplay = "Dates non spécifiées";
      }
      
      // Capture UTM SDR
      const storedUTMParams = getStoredUTMParams();
      const urlParams = new URLSearchParams(window.location.search);
      const sdr = urlParams.get('utm_sdr') || storedUTMParams.utm_sdr || '—';

      const slackMessage = `*Nouvelle demande*\n*Canal d'acquisition* : Formulaire\n*Market* : FR 🇫🇷\n*SDR* : ${sdr}\n*Source du lead* : Landing Lovable\n\n*E-mail pro* : ${email}\n*Type d'event* : ${eventTypeDisplay}\n*Capacité* : ${eventDetails.participantsCount} personnes\n*Dates* : ${dateDisplay}\n*Location* : ${eventDetails.location}\n*Budget* : ${budgetFormatted}`;

      await sendSlackMessage(slackMessage, "C0B0LPU0SCS");

      // Fermer le modal et nettoyer les données
      setShowEmailOnlyMissing(false);
      setSavedFormData(null);
      
      toast.success("Merci ! Notre équipe va se mettre immédiatement au travail pour vous proposer des lieux personnalisés.");

    } catch (error) {
      console.error("Email submission error:", error);
      toast.error("Erreur lors de l'envoi. Veuillez réessayer.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Toaster position="top-center" />
      <AnnouncementBanner />
      <Header onOpenForm={() => {
        setFormTrigger({ location: "header", text: "Envoyer votre demande" });
        setIsFormOpen(true);
      }} />
      <main className="flex-1">
        <HeroSection onOpenForm={() => {
          setFormTrigger({ location: "hero", text: "Commencer gratuitement" });
          setIsFormOpen(true);
        }} />
        <LogoBanner />
        <KeyEventSection onOpenForm={() => {
          setFormTrigger({ location: "key-event-section", text: "Faire une demande" });
          setIsFormOpen(true);
        }} />
        <CollectionIntroSection />
        <HowToSteps onOpenForm={() => {
          setFormTrigger({ location: "how-to-steps", text: "Je veux essayer" });
          setIsFormOpen(true);
        }} />
        <TestimonialsSection />
        <TotalControlSection onOpenForm={() => {
          setFormTrigger({ location: "total-control", text: "Commencer" });
          setIsFormOpen(true);
        }} />
        
        <OurPromise />
        
        <FundraisingSection onOpenForm={() => {
          setFormTrigger({ location: "fundraising", text: "Commencer" });
          setIsFormOpen(true);
        }} />
        
        {/* Formulaire modal de génération de leads */}
        <LeadFormModal 
          isOpen={isFormOpen} 
          onClose={() => setIsFormOpen(false)} 
          onFormClose={handleFormClose}
          initialFormData={savedFormData ? {
            eventDetails: savedFormData.eventDetails,
            contactInfo: savedFormData.contactInfo,
            startAtEmailStep: true
          } : undefined}
          triggerLocation={formTrigger.location}
          triggerText={formTrigger.text}
        />
        
        {/* Modal email manquant */}
        <EmailOnlyMissingModal
          isOpen={showEmailOnlyMissing}
          onClose={handleEmailOnlyMissingClose}
          onContinue={handleEmailOnlyMissingContinue}
          onSubmitEmail={handleEmailSubmit}
        />
      </main>
      <Footer />
    </div>
  );
};

export default Index;
